/**
 * Maps a legacy plan-name string baked into an audit-log snapshot back to its
 * current display name.
 *
 * `before`/`after`/`metadata` on an audit-log row are immutable snapshots of
 * whatever `plans.name` held at write time (see `apps/api/src/modules/
 * billing/services/subscription.service.ts` and `billing.service.ts`, both of
 * which write `plan.name` verbatim into `planName`/`targetPlanName`). Before
 * the 2026-09-03 migration
 * (`packages/database/schema-changes/2026-09-03-plan-display-names.sql`),
 * `plans.name` was seeded equal to `plans.code` (e.g. `scale-monthly-05-26`),
 * so any audit row written before that migration has the raw code
 * permanently baked in — these fields are a point-in-time snapshot, not a
 * live join, so a later rename in the `plans` table can't reach them. This
 * map only ever matches those legacy raw-code strings; a post-migration row
 * already carries the real display name, which isn't a key here, so it
 * passes through unchanged.
 *
 * Keys are `plans.code` values (`packages/database/src/seeds/plan-catalog.ts`
 * plus the 5 SH custom rows, which aren't in the seeded catalog). The three
 * `scale-plus-{5k,10k,20k}-*` code families per cadence are distinct
 * send-volume tiers of "Scale Plus" and get distinct labels (`5K`/`10K`/
 * `20K` suffix) so they don't render identically in the diff table.
 */
const LEGACY_PLAN_CODE_LABELS: Record<string, string> = {
  free: "Free",
  trial: "Trial",

  "starter-monthly-05-26": "Trulyinbox Starter Monthly",
  "starter-quarterly-05-26": "Trulyinbox Starter Quarterly",
  "starter-yearly-05-26": "Trulyinbox Starter Annually",

  "growth-monthly-05-26": "Trulyinbox Growth Monthly",
  "growth-quarterly-05-26": "Trulyinbox Growth Quarterly",
  "growth-yearly-05-26": "Trulyinbox Growth Annually",

  "scale-monthly-05-26": "Trulyinbox Scale Monthly",
  "scale-quarterly-05-26": "Trulyinbox Scale Quarterly",
  "scale-yearly-05-26": "Trulyinbox Scale Annually",

  "scale-plus-5k-monthly-05-26": "Trulyinbox Scale Plus Monthly 5K",
  "scale-plus-5k-quarterly-05-26": "Trulyinbox Scale Plus Quarterly 5K",
  "scale-plus-5k-yearly-05-26": "Trulyinbox Scale Plus Annually 5K",
  "scale-plus-10k-monthly-05-26": "Trulyinbox Scale Plus Monthly 10K",
  "scale-plus-10k-quarterly-05-26": "Trulyinbox Scale Plus Quarterly 10K",
  "scale-plus-10k-yearly-05-26": "Trulyinbox Scale Plus Annually 10K",
  "scale-plus-20k-monthly-05-26": "Trulyinbox Scale Plus Monthly 20K",
  "scale-plus-20k-quarterly-05-26": "Trulyinbox Scale Plus Quarterly 20K",
  "scale-plus-20k-yearly-05-26": "Trulyinbox Scale Plus Annually 20K",

  "sh-starter-dec-24": "SH Basic",
  "sh-pro-dec-24": "SH Pro",
  "sh-scale-dec-24": "SH Scale",
  "sh-scale+-dec-24": "SH Scale+",
  "sh-scale++-dec-24": "SH Scale++",
};

/**
 * Field keys inside an audit-log `before`/`after`/`metadata` blob that carry
 * a plan-name snapshot (see the billing-service call sites above). Resolution
 * is scoped to just these keys so an unrelated field can't be misread as a
 * plan code purely because its string value happens to collide with one
 * (e.g. `free`/`trial` are themselves plain English words).
 */
export const PLAN_NAME_SNAPSHOT_KEYS = new Set(["planName", "targetPlanName"]);

/**
 * Resolve a raw audit-log plan-name snapshot to its current display name.
 * Falls back to the raw value for anything not in the legacy map — already a
 * real display name (post-migration row), or genuinely unrecognised.
 */
export function planNameLabel(rawValue: string): string {
  return LEGACY_PLAN_CODE_LABELS[rawValue] ?? rawValue;
}
